import React, { useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { toast } from "react-hot-toast";

import { EvaluationForCandidate } from '../services/apiService';
import { getToken, getUserFromToken, getCompanyCodeFromToken } from '../services/authService';
import { SendApprovalWorkflowEmailAsync } from '../services/sendMailSMTP';

const PRIMARY_COLOR = 'rgb(18, 76, 114)';

const EvaluationCandidatePage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const candidateDetails = state?.candidateDetails;
  const initialCompetencies = state?.competencies;

  // Move hooks before early return
  const [competencies, setCompetencies] = useState(initialCompetencies || []);
  const [selectedHiringRecommendation, setSelectedHiringRecommendation] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [selectedInterviewIndex, setSelectedInterviewIndex] = useState('');
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preparedFormData, setPreparedFormData] = useState(null);
  
  // Get available interviews (only scheduled ones)
  const availableInterviews = candidateDetails?.interviews?.filter(
    interview => interview.status === "Interview Scheduled"
  ) || [];
  
  // Check if this is direct evaluation (no interview)
  const isDirectEvaluation = candidateDetails?.candidate?.status === 'Ready For Evaluation';
  const hasScheduledInterviews = availableInterviews.length > 0;

  // Early return logic
  if (!candidateDetails || !initialCompetencies) {
    return (
      <div className="p-6">
        <h2 className="text-lg font-semibold text-red-600">Invalid access or missing data</h2>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 bg-primary-600 text-white px-4 py-2 rounded"
        >
          Go Back
        </button>
      </div>
    );
  }

  // Handling hiring recommendation change
  const handleHiringRecommendationChange = (e) => {
    setSelectedHiringRecommendation(e.target.value);
  };

  // Handling additional comments change
  const handleAdditionalCommentsChange = (e) => {
    setAdditionalComments(e.target.value);
  };

  // Handling competency changes (performance expectation and comments)
  const handlecompetenciesChange = (competencyId, type, value) => {
    setCompetencies((prevCompetencies) =>
      prevCompetencies.map((comp) =>
        comp.competency_id === competencyId
          ? { ...comp, [type]: value }
          : comp
      )
    );
  };

  // Step 1: Validate and show confirmation popup
  const handlecompetenciesSubmit = (e) => {
    e.preventDefault();
    const userName = getUserFromToken();

    if (!selectedHiringRecommendation) {
      toast.error("Please select a hiring recommendation.");
      return;
    }

    if (!isDirectEvaluation && !selectedInterviewIndex && hasScheduledInterviews) {
      toast.error("Please select an interview.");
      return;
    }

    const missingExpectations = competencies.some(
      (comp) => !comp.performanceExpectation || comp.performanceExpectation.trim() === ""
    );

    if (missingExpectations) {
      toast.error("Please provide performance expectations for all competencies.");
      return;
    }

    if (!userName) {
      toast.error("Evaluator information missing.");
      return;
    }

    const formData = {
      candidate_id: candidateDetails?.candidate?.candidate_id,
      role: candidateDetails?.candidate?.role,
      hiring_recommendation: selectedHiringRecommendation,
      additional_comments: additionalComments,
      evaluated_by: userName,
      is_direct_evaluation: isDirectEvaluation,
      competencyEvaluations: competencies.map((comp) => ({
        competency_id: comp.competency_id,
        competency_name: comp.name,
        performance_expectation: comp.performanceExpectation || "",
        comments: comp.comments || "",
      })),
    };

    if (!isDirectEvaluation && selectedInterviewIndex !== '' && availableInterviews[selectedInterviewIndex]) {
      formData.interview_id = availableInterviews[selectedInterviewIndex]?.interview_id;
      formData.interview_date = availableInterviews[selectedInterviewIndex]?.interview_date;
    }

    setPreparedFormData(formData);
    setShowConfirmPopup(true);
  };

  // Step 2: Confirm and submit
  const handleConfirmSubmit = async () => {
    if (!preparedFormData) return;
    setIsSubmitting(true);
    try {
      const response = await EvaluationForCandidate(preparedFormData);

      if ([400, 500].includes(Number(response?.statusCode)) || response?.status === 500) {
        toast.error("Failed to submit the evaluation.", { position: "top-right" });
      } else if (response?.statusCode === 409 || response?.status === 409) {
        toast.error("An evaluation has already been submitted for this candidate.", {
          position: "top-right",
        });
      } else {
        toast.success("Evaluation submitted successfully!");
        setShowConfirmPopup(false);

        // Send email to recruiter
        try {
          const recruiterEmail = candidateDetails?.candidate?.recruiterEmail;
          if (recruiterEmail) {
            await SendApprovalWorkflowEmailAsync({
              recipientRole: '',
              companyCode: getCompanyCodeFromToken() || '',
              toEmails: recruiterEmail,
              requestId: candidateDetails?.hiringRequests?.request_id || 0,
              referenceNumber: candidateDetails?.candidate?.ReferenceNumber || '',
              action: "Evaluation Submitted",
              actorName: getUserFromToken(),
              actorRole: getToken()?.role || '',
              nextStep: "Review Evaluation",
              notes: `Evaluation for ${candidateDetails?.candidate?.full_name || 'candidate'}. Recommendation: ${preparedFormData.hiring_recommendation}`,
              department: candidateDetails?.candidate?.department_name || '',
              position: candidateDetails?.candidate?.role || '',
              requestType: candidateDetails?.hiringRequests?.request_type || '',
            });
          }
        } catch (emailErr) {
          console.error("Failed to send evaluation email:", emailErr);
        }

        setTimeout(() => {
          navigate(-1);
        }, 500);
      }
    } catch (err) {
      console.error("Error submitting evaluation:", err);
      toast.error("Something went wrong. Please try again.", { position: "top-right" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white mx-4 sm:pt-4 sm:px-4 sm:rounded-t-[32px] md:pt-8 md:px-[calc(5%-1rem)] md:rounded-t-[40px] lg:px-[calc(6%-1rem)] xl:pt-24 xl:px-[calc(2.5%-1rem)] xl:rounded-t-[48px] 2xl:px-[calc(8%-1rem)] 3xl:px-[calc(8%-1rem)] 4xl:px-[calc(10%-1rem)]">
      
      <div className='bg-white relative -top-[180px] p-6 rounded-2xl filter drop-shadow-[0_5px_30px_rgba(10,60,96,0.10)]'>
        
        {/* Candidate Info */}
        <div className="text-sm sm:text-base text-primary-800 border border-[#f5f3ef] rounded-lg p-5 bg-gradient-to-r from-primary-800/20 to-primary-700/80 shadow-sm mb-4">
          <div className="flex flex-col w-full gap-2">
            <div className='flex'>
              <div className='text-xl'><strong>Candidate:</strong> {candidateDetails?.candidate?.full_name || 'N/A'}</div>
              <div className='ml-auto'>
                <button onClick={() => navigate(-1)} className="w-full mt-auto md:w-auto bg-white hover:bg-primary-800/70 text-primary-800 hover:text-white px-6 py-2 rounded text-base xl:text-lg">
                  ← Back
                </button>
              </div>
            </div>
            <div><strong>Department:</strong> {candidateDetails?.candidate?.department_name || 'N/A'}</div>
            <div className="col-span-1 sm:col-span-2">
              <strong>Evaluator:</strong> {getToken().unique_name}
            </div>
            
            {/* Interview Selection - Only show if there are scheduled interviews */}
            {!isDirectEvaluation && hasScheduledInterviews && (
              <div>
                <strong>Interview Date:</strong>
                <div className="relative w-full md:w-1/3">
                  <select
                    className="w-full bg-white text-primary-800 font-semibold rounded-xl border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-600 px-5 py-2 appearance-none"
                    value={selectedInterviewIndex}
                    onChange={(e) => setSelectedInterviewIndex(e.target.value)}
                  >
                    <option value="" disabled>Choose Interview</option>
                    {availableInterviews.map((interview, index) => (
                      <option key={index} value={index}>
                        {new Date(interview.interview_date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-primary-800 font-bold">
                    <img className="w-3 h-3" src="/drop.svg" alt="dropdown"/>
                  </span>
                </div>
              </div>
            )}
            
            {/* Direct Evaluation Message */}
            {isDirectEvaluation && (
              <div className="mt-2 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <span className="text-teal-700 font-medium">📝 Direct Evaluation Mode</span>
                <p className="text-sm text-teal-600 mt-1">
                  This candidate is being evaluated without an interview. Please assess based on their application and documents.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Evaluation Form */}
        <form onSubmit={handlecompetenciesSubmit} className="space-y-10">
          {Array.from({ length: Math.ceil(competencies.length / 3) }, (_, rowIndex) => {
            const items = competencies.slice(rowIndex * 3, rowIndex * 3 + 3);

            return (
              <div key={rowIndex} className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 xl:grid-cols-3 gap-4">
                {items.map((comp, index) => (
                  <div key={comp.competency_id} className="bg-white rounded-2xl p-6 filter drop-shadow-[0_5px_30px_rgba(10,60,96,0.10)]">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-primary-800">
                        {rowIndex * 3 + index + 1}. {comp.name}
                      </h3>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Performance Expectation</p>
                      <div className="flex flex-wrap gap-3">
                        {['Does Not Meet', 'Meet', 'Exceed'].map((option) => {
                          return (
                            <label key={option} className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`expectation-${comp.competency_id}`}
                                value={option}
                                className="hidden peer"
                                onChange={() =>
                                  handlecompetenciesChange(comp.competency_id, 'performanceExpectation', option)
                                }
                              />
                              <span className="px-4 py-2 border border-primary-800 rounded-lg peer-checked:bg-primary-800 peer-checked:text-white peer-hover:bg-primary-800/10 peer-hover:border-primary-800/10 peer-hover:text-primary-800 text-sm">
                                {option}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Comments <span className="text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        name={`comment-${comp.competency_id}`}
                        rows="3"
                        className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-500 text-sm focus:outline-none focus:ring-1 focus:ring-primary-800/50"
                        placeholder="Share any feedback or observations..."
                        onChange={(e) =>
                          handlecompetenciesChange(comp.competency_id, 'comments', e.target.value)
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          })}

          {/* Hiring Recommendation */}
          <div className="mt-12 bg-primary-800/10 p-6 rounded-xl">
            <h3 className="text-lg font-semibold text-primary-800 mb-3">Hiring Recommendation</h3>

            <div className="flex flex-wrap gap-4">
              {['Outstanding Hire', 'Good Hire', 'Potential Hire', 'Unsuitable Hire', 'Proceed to hire', 'Hold', 'Rejected'].map((status) => (
                <label key={status} className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="hiringStatus"
                    value={status}
                    checked={selectedHiringRecommendation === status}
                    onChange={handleHiringRecommendationChange}
                    className="hidden peer"
                  />
                  <span className="px-4 py-2 border border-primary-800 rounded-lg peer-checked:bg-primary-800 peer-checked:text-white peer-hover:bg-primary-800/10 peer-hover:border-primary-800/10 peer-hover:text-primary-800 text-sm">
                    {status}
                  </span>
                </label>
              ))}
            </div>

            <div className="mt-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Comments <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                name="additional-comments"
                rows="4"
                className="w-full py-2 border px-2 border-primary-800/40 rounded-md text-gray-500 text-sm focus:outline-none focus:ring-1 focus:ring-primary-800/50"
                placeholder="Any other thoughts, concerns, or feedback about the candidate?"
                value={additionalComments}
                onChange={handleAdditionalCommentsChange}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-right mb-4">
            <button
              type="submit"
              className="bg-primary-800 hover:bg-primary-800/60 transition-colors text-white font-semibold px-8 mb-2 py-3 rounded-lg shadow-sm"
            >
              Submit Evaluation
            </button>
          </div>
        </form>
      </div>
      {/* Confirmation Popup */}
      {showConfirmPopup && preparedFormData && (
        <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderColor: 'rgba(99, 102, 241, 0.2)', borderWidth: '1px' }}>
            <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(99, 102, 241, 0.1)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)' }}>
                  <svg className="w-5 h-5" style={{ color: '#6366f1' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold" style={{ color: PRIMARY_COLOR }}>Confirm Evaluation</h2>
                  <p className="text-xs mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Review before submitting</p>
                </div>
              </div>
              <button onClick={() => setShowConfirmPopup(false)} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-5 sm:p-6 space-y-4">
              {/* Candidate Info */}
              <div className="grid grid-cols-2 gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.08)' }}>
                <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Candidate</span><span className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{candidateDetails?.candidate?.full_name}</span></div>
                <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Type</span><span className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{isDirectEvaluation ? 'Direct Evaluation' : 'Interview Based'}</span></div>
                <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Evaluator</span><span className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{preparedFormData.evaluated_by}</span></div>
                <div><span className="text-xs font-medium block" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Department</span><span className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{candidateDetails?.candidate?.department_name || 'N/A'}</span></div>
              </div>

              {/* Recommendation */}
              <div className="flex items-center gap-3 p-4 rounded-xl" style={{ backgroundColor: preparedFormData.hiring_recommendation === 'Do Not Recommend' ? 'rgba(239, 68, 68, 0.05)' : 'rgba(5, 150, 105, 0.05)', border: `1px solid ${preparedFormData.hiring_recommendation === 'Do Not Recommend' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(5, 150, 105, 0.15)'}` }}>
                <svg className="w-6 h-6 flex-shrink-0" style={{ color: preparedFormData.hiring_recommendation === 'Do Not Recommend' ? '#ef4444' : '#059669' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={preparedFormData.hiring_recommendation === 'Do Not Recommend' ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} />
                </svg>
                <div>
                  <span className="text-xs" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>Hiring Recommendation</span>
                  <p className="text-sm font-bold" style={{ color: preparedFormData.hiring_recommendation === 'Do Not Recommend' ? '#ef4444' : '#059669' }}>{preparedFormData.hiring_recommendation}</p>
                </div>
              </div>

              {/* Competencies */}
              {preparedFormData.competencyEvaluations?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold mb-2" style={{ color: PRIMARY_COLOR }}>Competency Ratings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {preparedFormData.competencyEvaluations.map((comp, idx) => {
                      const rc = (comp.performance_expectation || '').toLowerCase().includes('exceed') ? '#059669' : (comp.performance_expectation || '').toLowerCase().includes('below') ? '#ef4444' : '#d97706';
                      return (
                        <div key={idx} className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.02)', border: '1px solid rgba(18,76,114,0.06)' }}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold" style={{ color: PRIMARY_COLOR }}>{comp.competency_name}</span>
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ml-2" style={{ backgroundColor: `${rc}12`, color: rc }}>{comp.performance_expectation}</span>
                          </div>
                          {comp.comments && <p className="text-[11px] leading-relaxed text-gray-500">{comp.comments}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              
            </div>

            <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(99, 102, 241, 0.1)', backgroundColor: 'rgba(99, 102, 241, 0.02)' }}>
              <button onClick={() => setShowConfirmPopup(false)} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: PRIMARY_COLOR, borderColor: 'rgba(18, 76, 114, 0.2)' }}>Back to Edit</button>
              <button onClick={handleConfirmSubmit} disabled={isSubmitting} className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all" style={{ backgroundColor: isSubmitting ? 'rgba(99, 102, 241, 0.5)' : '#6366f1', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                {isSubmitting ? 'Submitting...' : 'Confirm & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationCandidatePage;